// filepath: apps/web/src/features/admin/userRoles/BulkAssignPopover.tsx
//
// FR-UI-ADM-5 AC-4 — bulk-assign dialog.
//
// Renders when the user has selected N≥2 rows. Submits POST
// /api/admin/users/roles/bulk-assign with X-Idempotency-Key
// (ARCH Rule 3). On partial success shows
// `{assigned: N, failed: [{user_id, error}]}` with a collapsible
// failure list. The popover stays visible for retry; the SPA
// never auto-retries partial failures (defense-in-depth).

import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBulkAssignRoles } from "./queries";

interface BulkAssignPopoverProps {
  userIds: string[];
  currentVersion: number;
  onComplete: () => void;
}

export function BulkAssignPopover({
  userIds,
  currentVersion,
  onComplete,
}: BulkAssignPopoverProps): React.ReactElement {
  const { t } = useTranslation();
  const bulkMutation = useBulkAssignRoles();
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set());
  const [failuresExpanded, setFailuresExpanded] = useState(false);
  const [partialSuccess, setPartialSuccess] = useState<{
    assigned: number;
    failed: Record<string, string>;
  } | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onComplete();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onComplete]);

  const onSubmit = async () => {
    const req = {
      user_ids: userIds,
      roles: Array.from(selectedRoles),
      expected_sync_state_version: currentVersion,
    };
    try {
      const resp = await bulkMutation.mutateAsync(req);
      setPartialSuccess({ assigned: resp.assigned, failed: resp.failed });
      if (resp.failed && Object.keys(resp.failed).length > 0) {
        setFailuresExpanded(true);
      } else {
        // Full success — close the popover.
        onComplete();
      }
    } catch {
      // error rendered below.
    }
  };

  return (
    <dialog
      ref={dialogRef}
      open
      role="dialog"
      aria-modal="true"
      aria-labelledby="bulk-assign-title"
      data-testid="bulk-assign-popover"
    >
      <h2 id="bulk-assign-title">
        {t("admin.users.dialog.bulk_assign.title", { count: userIds.length })}
      </h2>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void onSubmit();
        }}
      >
        <fieldset>
          <legend>{t("admin.users.dialog.role_picker.label.roles")}</legend>
          {/* V1: roles are typed in as a comma-separated list. */}
          <input
            type="text"
            placeholder={t("admin.users.dialog.bulk_assign.roles_placeholder")}
            data-testid="bulk-assign-roles-input"
            onChange={(e) => {
              const next = new Set(
                e.target.value
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean),
              );
              setSelectedRoles(next);
            }}
          />
        </fieldset>
        <button
          type="submit"
          disabled={bulkMutation.isPending || selectedRoles.size === 0}
          data-testid="bulk-assign-save"
        >
          {t("admin.users.dialog.bulk_assign.cta", { count: userIds.length })}
        </button>
        <button
          type="button"
          onClick={onComplete}
          data-testid="bulk-assign-cancel"
        >
          {t("common.cancel")}
        </button>
      </form>

      {bulkMutation.isError ? (
        <div role="alert" data-state="save_error">
          {bulkMutation.error?.message ??
            t("admin.users.dialog.bulk_assign.error")}
        </div>
      ) : null}

      {partialSuccess ? (
        <div
          data-state="partial_success"
          data-testid="bulk-assign-partial-success"
        >
          <p>
            {t("admin.users.dialog.bulk_assign.partial_success", {
              assigned: partialSuccess.assigned,
            })}
          </p>
          {Object.keys(partialSuccess.failed).length > 0 ? (
            <details
              open={failuresExpanded}
              onToggle={(e) =>
                setFailuresExpanded((e.target as HTMLDetailsElement).open)
              }
            >
              <summary data-testid="bulk-assign-failures-toggle">
                {t("admin.users.dialog.bulk_assign.failures_label", {
                  count: Object.keys(partialSuccess.failed).length,
                })}
              </summary>
              <ul>
                {Object.entries(partialSuccess.failed).map(([uid, err]) => (
                  <li key={uid} data-user-id={uid}>
                    {uid}: {err}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
          <button
            type="button"
            onClick={() => {
              setPartialSuccess(null);
              setFailuresExpanded(false);
              onComplete();
            }}
            data-testid="bulk-assign-dismiss"
          >
            {t("admin.users.dialog.bulk_assign.dismiss")}
          </button>
        </div>
      ) : null}
    </dialog>
  );
}
